// src/components/inventory/LowStockAlerts.js
// Enhanced Low Stock Alerts component with modern UI
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • FlowSense AI on every alert (pass `insights` — the AI overview items): days of cover,
//     run-out date, ML re-order point and the suggested order quantity / value; alerts are
//     sorted so the item that runs out first is on top.
//   • "Quick reorder" and "Create Purchase Order" work: onReorder(alert) opens a Receive stock
//     movement, onCreatePO(alert) opens Smart Reorder for that item. Buttons without a handler
//     are hidden instead of doing nothing.
//   • Reads the API field names (warehouse_location / category_name / alert_triggered_at /
//     dynamic_reorder_point) as well as the old ones; "Dismiss all"; collapsible list.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useMemo } from 'react'; // --- UPDATE: + useMemo
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  CloseButton,
  VStack,
  Text,
  HStack,
  Badge,
  Button,
  Icon,
  Collapse,
  useColorModeValue,
  Progress,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import {
  MdWarning,
  MdExpandMore,
  MdExpandLess,
  MdShoppingCart,
  MdVisibility
} from 'react-icons/md';
import { MdAutoAwesome, MdAddBox } from 'react-icons/md'; // --- UPDATE
import { motion, AnimatePresence } from 'framer-motion';
import { RISK_META, money } from './inventoryAiIntel'; // --- UPDATE

const MotionBox = motion(Box);

const LowStockAlerts = ({ alerts, onDismiss, insights = null, onReorder = null, onCreatePO = null, onDismissAll = null }) => { // --- UPDATE: + insights, onReorder, onCreatePO, onDismissAll
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());
  const [collapsed, setCollapsed] = useState(false); // --- UPDATE
  const [showAll, setShowAll] = useState(false); // --- UPDATE

  // Theme colors - hooks called at top level
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const progressBg = useColorModeValue('gray.100', 'gray.600');

  // ✅ FIX: Move this hook here, outside the loop
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const aiBg = useColorModeValue('teal.50', 'whiteAlpha.100'); // --- UPDATE

  // --- UPDATE: FlowSense insight per alert (by item id, else by name) + sort by urgency
  const insightFor = useMemo(() => {
    const byId = new Map(); const byName = new Map();
    (Array.isArray(insights) ? insights : []).forEach((i) => { byId.set(String(i.id), i); if (i.name) byName.set(String(i.name).toLowerCase(), i); });
    return (a) => (a.item_id != null && byId.get(String(a.item_id))) || byName.get(String(a.name || '').toLowerCase()) || null;
  }, [insights]);
  const sorted = useMemo(() => {
    const list = Array.isArray(alerts) ? [...alerts] : [];
    const key = (a) => { const i = insightFor(a); return i ? (i.quantity <= 0 ? -1 : i.days_of_cover ?? 1e6) : 1e7; };
    return list.sort((a, b) => key(a) - key(b));
  }, [alerts, insightFor]);
  const visible = showAll ? sorted : sorted.slice(0, 6);
  // --- END UPDATE

  if (!alerts || alerts.length === 0) {
    return null;
  }

  const toggleAlert = (id) => {
    const newExpanded = new Set(expandedAlerts);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedAlerts(newExpanded);
  };

  const getStockPercentage = (quantity, threshold) => {
    if (!threshold || threshold === 0) return 0;
    return Math.min(100, (quantity / threshold) * 100);
  };

  const getStatusColor = (percentage) => {
    if (percentage < 25) return 'red';
    if (percentage < 50) return 'orange';
    return 'yellow';
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <VStack spacing={4} mb={6} align="stretch" role="region" aria-label="Low stock alerts">
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <Icon as={MdWarning} color="orange.500" boxSize={6} />
            <Text fontSize="xl" fontWeight="800" color={textColor}>
              Low Stock Alerts
            </Text>
            <Badge
              colorScheme="orange"
              fontSize="sm"
              borderRadius="full"
              px={3}
              py={1}
            >
              {alerts.length} {alerts.length === 1 ? 'item' : 'items'}
            </Badge>
          </HStack>
          {/* --- UPDATE: list controls */}
          <HStack spacing={1}>
            {onDismissAll && alerts.length > 1 && <Button size="xs" variant="ghost" onClick={() => onDismissAll(alerts.map((a) => a.id))}>Dismiss all</Button>}
            <IconButton size="sm" variant="ghost" icon={<Icon as={collapsed ? MdExpandMore : MdExpandLess} />} aria-label={collapsed ? 'Show low stock alerts' : 'Hide low stock alerts'} onClick={() => setCollapsed((c) => !c)} />
          </HStack>
        </HStack>

        <Collapse in={!collapsed} animateOpacity>
        <VStack spacing={4} align="stretch">
        <AnimatePresence>
          {visible.map((alert, index) => { /* --- UPDATE: sorted + paged */
            const isExpanded = expandedAlerts.has(alert.id);
            // --- UPDATE: the alert fires on the higher of the manual and the AI (dynamic) threshold
            const ai = insightFor(alert);
            const trigger = Math.max(Number(alert.low_stock_threshold) || 0, Number(alert.dynamic_reorder_point) || 0, Number(ai?.effective_reorder_point) || 0) || alert.low_stock_threshold;
            const stockPercentage = getStockPercentage(ai ? ai.quantity : alert.quantity, trigger);
            // --- END UPDATE
            const statusColor = getStatusColor(stockPercentage);
            const qty = ai ? ai.quantity : alert.quantity; // --- UPDATE: live quantity from FlowSense

            return (
              <MotionBox
                key={alert.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Alert
                  status="warning"
                  borderRadius="2xl"
                  borderWidth="2px"
                  borderColor={`${statusColor}.300`}
                  boxShadow="md"
                  position="relative"
                  overflow="hidden"
                  _hover={{
                    boxShadow: 'lg',
                    borderColor: `${statusColor}.400`,
                  }}
                  transition="all 0.2s"
                >
                  {/* Background decoration */}
                  <Box
                    position="absolute"
                    top={0}
                    right={0}
                    w="150px"
                    h="150px"
                    bgGradient={`linear(to-br, ${statusColor}.100, transparent)`}
                    opacity={0.3}
                    borderRadius="full"
                    transform="translate(40%, -40%)"
                  />

                  <AlertIcon boxSize={6} />

                  <Box flex="1">
                    <HStack justify="space-between" align="start" mb={2}>
                      <Box flex="1">
                        <HStack spacing={2} mb={1} flexWrap="wrap">
                          <AlertTitle fontSize="md" fontWeight="700" color={textColor}>
                            {alert.name}
                          </AlertTitle>
                          <Badge
                            colorScheme={statusColor}
                            fontSize="xs"
                            borderRadius="md"
                          >
                            {stockPercentage.toFixed(0)}% stock
                          </Badge>
                          {/* --- UPDATE */}
                          {ai && <Badge colorScheme={RISK_META[ai.risk]?.scheme || 'gray'} fontSize="xs" borderRadius="md">{RISK_META[ai.risk]?.label || ai.risk}</Badge>}
                        </HStack>

                        <AlertDescription fontSize="sm" color={mutedTextColor}>
                          Current: <Text as="span" fontWeight="600" color={`${statusColor}.500`}>
                            {qty}
                          </Text> / Threshold: <Text as="span" fontWeight="600">
                            {alert.low_stock_threshold}
                          </Text>
                          {/* --- UPDATE */}
                          {alert.dynamic_reorder_point ? <> · AI threshold <Text as="span" fontWeight="600">{alert.dynamic_reorder_point}</Text></> : null}
                        </AlertDescription>
                        {/* --- UPDATE: FlowSense line */}
                        {ai && (
                          <HStack mt={1.5} spacing={2} bg={aiBg} px={2} py={1} borderRadius="md" fontSize="xs" flexWrap="wrap">
                            <Icon as={MdAutoAwesome} color="teal.400" />
                            <Text>{ai.quantity <= 0 ? 'Out of stock now' : ai.days_of_cover != null ? `${ai.days_of_cover} days of cover${ai.stockout_date ? ` (≈ ${ai.stockout_date})` : ''}` : 'No recent usage'}</Text>
                            <Text>· lead time {ai.lead_time_days} d</Text>
                            {ai.suggested_order_qty > 0 && <Text fontWeight="700" color="teal.600">· order {ai.suggested_order_qty}{ai.suggested_order_value != null ? ` (${money(ai.suggested_order_value)})` : ''}</Text>}
                          </HStack>
                        )}
                      </Box>

                      <HStack spacing={1}>
                        {onReorder && ( /* --- UPDATE: wired (was a button with no action) */
                        <Tooltip label="Quick reorder" hasArrow>
                          <IconButton
                            icon={<Icon as={MdShoppingCart} />}
                            size="sm"
                            colorScheme="orange"
                            variant="ghost"
                            borderRadius="lg"
                            aria-label="Reorder"
                            onClick={() => onReorder({ ...alert, quantity: qty, insight: ai })} // --- UPDATE
                          />
                        </Tooltip>
                        )}

                        <Tooltip label={isExpanded ? 'Hide details' : 'Show details'} hasArrow>
                          <IconButton
                            icon={<Icon as={isExpanded ? MdExpandLess : MdExpandMore} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleAlert(alert.id)}
                            borderRadius="lg"
                            aria-label="Toggle details"
                          />
                        </Tooltip>

                        <CloseButton
                          size="sm"
                          onClick={() => onDismiss(alert.id)}
                          borderRadius="lg"
                          aria-label={`Dismiss ${alert.name}`} // --- UPDATE
                        />
                      </HStack>
                    </HStack>

                    {/* Stock level progress */}
                    <Progress
                      value={stockPercentage}
                      size="sm"
                      colorScheme={statusColor}
                      borderRadius="full"
                      bg={progressBg}
                      mb={2}
                    />

                    {/* Expandable details */}
                    <Collapse in={isExpanded} animateOpacity>
                      <Box
                        mt={3}
                        pt={3}
                        borderTopWidth="1px"
                        borderColor={borderColor} // ✅ FIX: Use the variable here
                      >
                        <VStack align="stretch" spacing={2}>
                          <HStack justify="space-between">
                            <Text fontSize="sm" color={mutedTextColor}>
                              SKU:
                            </Text>
                            <Text fontSize="sm" fontWeight="600" fontFamily="mono">
                              {alert.sku || ai?.sku || 'N/A'}
                            </Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color={mutedTextColor}>
                              Category:
                            </Text>
                            <Badge colorScheme="gray" fontSize="xs" borderRadius="md">
                              {alert.category || alert.category_name || ai?.category || 'Uncategorized'}
                            </Badge>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color={mutedTextColor}>
                              Location:
                            </Text>
                            <Text fontSize="sm" fontWeight="600">
                              {alert.location || alert.warehouse_location || ai?.location || 'Unknown'}
                            </Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color={mutedTextColor}>
                              Last Updated:
                            </Text>
                            <Text fontSize="sm" fontWeight="600">
                              {(alert.updated_at || alert.alert_triggered_at) // --- UPDATE: API field
                                ? new Date(alert.updated_at || alert.alert_triggered_at).toLocaleDateString()
                                : 'N/A'
                              }
                            </Text>
                          </HStack>

                          {/* --- UPDATE: FlowSense ML numbers */}
                          {ai && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color={mutedTextColor}>FlowSense re-order point / EOQ:</Text>
                              <Text fontSize="sm" fontWeight="600">{ai.effective_reorder_point ?? '—'} / {ai.eoq ?? '—'}</Text>
                            </HStack>
                          )}

                          <HStack mt={2} spacing={2}>
                          {onReorder && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="teal"
                            leftIcon={<Icon as={MdAddBox} />}
                            borderRadius="lg"
                            onClick={() => onReorder({ ...alert, quantity: qty, insight: ai })}
                          >
                            Receive stock
                          </Button>
                          )}
                          {onCreatePO && ( /* --- UPDATE: wired */
                          <Button
                            size="sm"
                            colorScheme="orange"
                            leftIcon={<Icon as={MdShoppingCart} />}
                            borderRadius="lg"
                            fontWeight="600"
                            onClick={() => onCreatePO({ ...alert, insight: ai })} // --- UPDATE
                          >
                            Create Purchase Order
                          </Button>
                          )}
                          </HStack>
                        </VStack>
                      </Box>
                    </Collapse>
                  </Box>
                </Alert>
              </MotionBox>
            );
          })}
        </AnimatePresence>
        {/* --- UPDATE */}
        {sorted.length > 6 && <Button size="sm" variant="ghost" leftIcon={<Icon as={MdVisibility} />} onClick={() => setShowAll((s) => !s)}>{showAll ? 'Show fewer' : `Show all ${sorted.length} alerts`}</Button>}
        </VStack>
        </Collapse>
      </VStack>
    </MotionBox>
  );
};

export default LowStockAlerts;
